import React, { ReactElement, useCallback, useEffect, useRef, useState } from "react";
import styled from "styled-components";
import { Button, Icon, Loader, Message } from "semantic-ui-react";

import { getResourceUrl } from "api/resources";
import { useResourceViewer } from "hooks/useResourceViewer";

type LoadState =
  | { status: "loading" }
  | { status: "ready"; url: string }
  | { status: "error" };

/**
 * Full-screen, in-tab viewer for PDF resources. Rendered once at the app
 * level; opens whenever the URL has `?resource=`.
 */
export default function ResourceViewer(): ReactElement | null {
  const { current, close } = useResourceViewer();
  const [state, setState] = useState<LoadState>({ status: "loading" });
  const backButton = useRef<HTMLButtonElement>(null);

  const key = current?.key;

  const load = useCallback(async (resourceKey: string, isActive: () => boolean) => {
    setState({ status: "loading" });
    try {
      const url = await getResourceUrl(resourceKey);
      if (isActive()) setState({ status: "ready", url });
    } catch {
      if (isActive()) setState({ status: "error" });
    }
  }, []);

  useEffect(() => {
    if (!key) return;
    let active = true;
    load(key, () => active);
    return () => {
      active = false;
    };
  }, [key, load]);

  // lock page scroll, close on Escape, and move focus into the viewer
  useEffect(() => {
    if (!key) return;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    backButton.current?.focus();

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") close();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [key, close]);

  if (!current) return null;

  return (
    <Overlay
      role="dialog"
      aria-modal="true"
      aria-label={current.title}
      data-testid="resource-viewer"
    >
      <Toolbar>
        <button
          ref={backButton}
          type="button"
          className="back"
          onClick={close}
          data-testid="resource-viewer-back"
        >
          <Icon name="arrow left" />
          <span>Back</span>
        </button>
        <h1 className="title" title={current.title}>
          {current.title}
        </h1>
        {state.status === "ready" && (
          <a
            className="open-external"
            href={state.url}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="resource-viewer-external"
          >
            <Icon name="external alternate" />
            <span>Open in new tab</span>
          </a>
        )}
      </Toolbar>

      <Body>
        {state.status === "loading" && (
          <Loader active inline="centered" content="Loading document…" />
        )}
        {state.status === "error" && (
          <Message negative data-testid="resource-viewer-error">
            <Message.Header>We couldn't open this document.</Message.Header>
            <p>Check your connection and try again.</p>
            <Button
              basic
              color="red"
              onClick={() => load(current.key, () => true)}
            >
              Try again
            </Button>
          </Message>
        )}
        {state.status === "ready" && (
          <iframe
            src={state.url}
            title={current.title}
            data-testid="resource-viewer-frame"
          />
        )}
      </Body>
    </Overlay>
  );
}

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: 900; /* under Semantic UI modals (1000+) so login still works */
  display: flex;
  flex-direction: column;
  background: #f4f6f9;
`;

const Toolbar = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  height: 56px;
  padding: 0 12px;
  background: #fff;
  border-bottom: 1px solid ${(props) => props.theme.tertiary};

  .back,
  .open-external {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    flex-shrink: 0;
    padding: 8px 12px;
    border: 1px solid ${(props) => props.theme.tertiary};
    border-radius: 6px;
    background: #fff;
    color: ${(props) => props.theme.primary};
    font-weight: 600;
    cursor: pointer;
    text-decoration: none;

    &:hover,
    &:focus-visible {
      border-color: ${(props) => props.theme.secondary};
      outline: none;
      box-shadow: 0 0 0 3px rgba(99, 160, 219, 0.2);
    }

    i.icon {
      margin: 0;
    }
  }

  .title {
    flex: 1;
    min-width: 0;
    margin: 0;
    font-size: 1.05em;
    font-weight: 600;
    color: ${(props) => props.theme.primary};
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  @media (max-width: 600px) {
    .open-external span {
      display: none;
    }
  }
`;

const Body = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 0;

  iframe {
    width: 100%;
    height: 100%;
    border: 0;
    background: #fff;
  }

  .ui.message {
    max-width: 420px;
  }
`;
